import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

export function DeleteConfirmDialog({ open, onOpenChange, onConfirm, title = "Permanently delete?", description, protectedWarning, count }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setText(""); setBusy(false); } }, [open]);

  const confirm = async () => {
    if (text !== "DELETE") return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); onOpenChange(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" /> {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {description || "Are you sure you want to permanently delete this client/event and all related records? This action cannot be undone."}
        </p>
        {protectedWarning && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 p-3 text-sm flex gap-2 mt-1" data-testid="delete-protected-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{protectedWarning}</span>
          </div>
        )}
        <div className="mt-2">
          <p className="text-sm mb-2">Type <span className="font-mono font-semibold">DELETE</span> to confirm.</p>
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="DELETE" className="df-input" data-testid="delete-confirm-input" autoFocus />
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={() => onOpenChange(false)} className="btn-outline flex-1" data-testid="delete-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={text !== "DELETE" || busy} className="flex-1 bg-destructive text-white hover:bg-destructive/90" data-testid="delete-confirm-button">
            {busy ? "Deleting…" : `Delete${count ? ` ${count}` : ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteConfirmDialog;
